// PushInit — глобальная инициализация Web Push (монтируется один раз в __root).
//
// Закрывает три дыры (см. audit 2026-06-01 «цепочка уведомлений»):
//   1. Pre-prompt после онбординга — по умолчанию запрашиваем разрешение на
//      браузерные уведомления у пользователя, который завершил онбординг и
//      которого ещё не спрашивали (Notification.permission === 'default').
//   2. Авто-восстановление — если разрешение уже выдано и в preferences
//      webPushEnabled=true, но активной подписки нет (новое устройство, чистка
//      браузера) — тихо переподписываемся и регистрируем токен.
//   3. Ротация подписки — SW шлёт `push:subscription-changed` при смене endpoint
//      браузером → переподписываемся.
//
// Показывается только authenticated-пользователю вне overlay-флоу (consent /
// onboarding). Один раз помечает показ через localStorage, чтобы не спамить.

import { toast } from "@studyhub/ui/toast";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PushPermissionPrompt } from "@/components/notifications/push-permission-prompt";
import {
	markPushPromptSeen as markSeen,
	readPushPromptSeen as readSeen,
} from "@/components/notifications/push-prompt-seen";
import { useWebPushChannel } from "@/hooks/use-web-push-channel";
import { api } from "@/lib/trpc-client";
import { useAuth } from "@/providers/auth-provider";

export function PushInit() {
	const { t } = useTranslation("notifications");
	const {
		isAuthenticated,
		isLoading,
		isSessionPending,
		isOffline,
		pendingConsent,
		pendingOnboarding,
	} = useAuth();
	const channel = useWebPushChannel();
	const utils = api.useUtils();

	const ready =
		isAuthenticated &&
		!isLoading &&
		!isSessionPending &&
		!isOffline &&
		!pendingConsent &&
		!pendingOnboarding;

	const prefsQuery = api.notifications.getPreferences.useQuery(undefined, {
		enabled: ready,
		staleTime: 60_000,
	});
	const updateMutation = api.notifications.updatePreferences.useMutation();

	const [promptOpen, setPromptOpen] = useState(false);

	const { enable, supported, vapidConfigured, permission, isSubscribed, isBusy } = channel;
	const webPushEnabled = prefsQuery.data?.webPushEnabled ?? false;

	// (2) Авто-восстановление подписки: разрешение есть + включено в настройках,
	// но активной подписки нет → тихо переподписываемся.
	useEffect(() => {
		if (!ready || !supported || !vapidConfigured) return;
		if (permission !== "granted") return;
		if (isSubscribed || isBusy) return;
		if (!webPushEnabled) return;
		void enable();
	}, [ready, supported, vapidConfigured, permission, isSubscribed, isBusy, webPushEnabled, enable]);

	// (3) Ротация подписки браузером — SW шлёт postMessage.
	useEffect(() => {
		if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
		const handler = (event: MessageEvent): void => {
			if (event.data?.type === "push:subscription-changed") {
				void enable();
			}
		};
		navigator.serviceWorker.addEventListener("message", handler);
		return () => navigator.serviceWorker.removeEventListener("message", handler);
	}, [enable]);

	// (1) Pre-prompt после онбординга — один раз, если разрешение ещё не запрошено.
	useEffect(() => {
		if (!ready || !supported || !vapidConfigured) return;
		if (permission !== "default") return;
		if (readSeen()) return;
		setPromptOpen(true);
	}, [ready, supported, vapidConfigured, permission]);

	const handleAllow = useCallback(async () => {
		setPromptOpen(false);
		markSeen();
		const res = await enable();
		if (res.ok) {
			updateMutation.mutate(
				{ webPushEnabled: true },
				{ onSuccess: () => void utils.notifications.getPreferences.invalidate() },
			);
			toast.success(t("pushPermission.enabled"));
		} else if (res.reason === "denied") {
			toast.message(t("pushPermission.deniedHint"));
		}
	}, [enable, updateMutation, utils, t]);

	const handleLater = useCallback(() => {
		setPromptOpen(false);
		markSeen();
	}, []);

	if (!promptOpen) return null;

	return (
		<PushPermissionPrompt
			open={promptOpen}
			onOpenChange={(open) => {
				if (!open) handleLater();
			}}
			onAllow={() => void handleAllow()}
			onLater={handleLater}
		/>
	);
}
